import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import SalesChart from './SalesChart';
import apiService from '../../services/api';

const DashboardGrid = styled.div`
  display: grid;
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-8);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-8);
`;

const StatCard = styled.div<{ trend?: 'up' | 'down' | 'neutral' }>`
  background: white;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  border-left: 4px solid ${props => 
    props.trend === 'up' ? '#10b981' : 
    props.trend === 'down' ? '#ef4444' : 
    'var(--color-primary)'};
  
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-3);
  }
  
  .icon {
    font-size: 2rem;
  }
  
  .trend {
    font-size: var(--font-size-sm);
    font-weight: 500;
    color: ${props => 
      props.trend === 'up' ? '#10b981' : 
      props.trend === 'down' ? '#ef4444' : 
      'var(--color-text-light)'};
  }
  
  .value {
    font-size: var(--font-size-3xl);
    font-weight: 700;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-1);
  }
  
  .label {
    color: var(--color-text-light);
    font-size: var(--font-size-sm);
    margin-bottom: var(--spacing-2);
  }
  
  .sub-stats {
    display: flex;
    gap: var(--spacing-4);
    font-size: var(--font-size-xs);
    color: var(--color-text-light);
  }
`;

const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-8);
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  
  h3 {
    color: var(--color-secondary);
    margin-bottom: var(--spacing-4);
    display: flex;
    align-items: center;
    justify-content: space-between);
  }
  
  .chart-placeholder {
    height: 300px;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: var(--border-radius);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-light);
    font-style: italic;
  }
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-4);
`;

const KPICard = styled.div`
  background: white;
  padding: var(--spacing-4);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  text-align: center;
  
  .kpi-value {
    font-size: var(--font-size-xl);
    font-weight: 600;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-1);
  }
  
  .kpi-label {
    font-size: var(--font-size-xs);
    color: var(--color-text-light);
  }
`;

const RecentActivity = styled.div`
  background: white;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  
  h3 {
    color: var(--color-secondary);
    margin-bottom: var(--spacing-4);
  }
  
  .activity-item {
    padding: var(--spacing-3);
    border-bottom: 1px solid var(--color-border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    &:last-child {
      border-bottom: none;
    }
    
    .activity-content {
      flex: 1;
    }
    
    .activity-type {
      font-weight: 500;
      margin-bottom: var(--spacing-1);
    }
    
    .activity-details {
      font-size: var(--font-size-sm);
      color: var(--color-text-light);
    }
    
    .time {
      font-size: var(--font-size-xs);
      color: var(--color-text-light);
      white-space: nowrap;
      margin-left: var(--spacing-3);
    }
  }
`;

const TopProducts = styled.div`
  background: white;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  
  h3 {
    color: var(--color-secondary);
    margin-bottom: var(--spacing-4);
  }
  
  .product-item {
    display: flex;
    align-items: center;
    padding: var(--spacing-3);
    border-bottom: 1px solid var(--color-border);
    
    &:last-child {
      border-bottom: none;
    }
    
    .product-emoji {
      font-size: 2rem;
      margin-right: var(--spacing-3);
    }
    
    .product-info {
      flex: 1;
      
      .name {
        font-weight: 500;
        margin-bottom: var(--spacing-1);
      }
      
      .sales {
        font-size: var(--font-size-sm);
        color: var(--color-text-light);
      }
    }
    
    .revenue {
      font-weight: 600;
      color: var(--color-secondary);
    }
  }
`;

const AdminDashboard: React.FC = () => {
  const [chartPeriod, setChartPeriod] = useState('Senaste 30 dagarna');
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [orderStats, productStats, topProductsData] = await Promise.all([
        apiService.getOrderStatistics(),
        apiService.getProductStatistics(),
        apiService.getTopProducts(5)
      ]);

      // Combine the data
      const combinedData = {
        ...(orderStats || {}),
        ...(productStats || {})
      };

      setDashboardData(combinedData);
      setTopProducts(Array.isArray(topProductsData) ? topProductsData : []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set fallback data on error
      setDashboardData({
        total_revenue: 0,
        monthly_revenue: 0,
        total_orders: 0,
        not_shipped: 0,
        total_products: 0,
        active_products: 0,
        avg_order_value: 0,
        total_net_profit: 0,
        monthly_net_profit: 0,
        daily_net_profit: 0
      });
      setTopProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const stats = dashboardData ? [
    { 
      icon: '💰', 
      value: `${Number(dashboardData.total_revenue || 0).toLocaleString('sv-SE')} kr`, 
      label: 'Total omsättning',
      trend: 'up' as const,
      trendValue: Number(dashboardData.monthly_revenue || 0) > 0 ? 'Månad aktiv' : 'Ingen aktivitet',
      subStats: [
        `Denna månad: ${Number(dashboardData.monthly_revenue || 0).toLocaleString('sv-SE')} kr`, 
        `Daglig: ${Number(dashboardData.daily_revenue || 0).toLocaleString('sv-SE')} kr`
      ]
    },
    { 
      icon: '💸', 
      value: `${Number(dashboardData.total_net_profit || 0).toLocaleString('sv-SE')} kr`, 
      label: 'Nettovinst',
      trend: Number(dashboardData.total_net_profit || 0) > 0 ? 'up' as const : (Number(dashboardData.total_net_profit || 0) < 0 ? 'down' as const : 'neutral' as const),
      trendValue: Number(dashboardData.monthly_net_profit || 0) > 0 ? 'Positiv denna månad' : (Number(dashboardData.monthly_net_profit || 0) < 0 ? 'Negativ denna månad' : 'Oförändrad'),
      subStats: [
        `Denna månad: ${Number(dashboardData.monthly_net_profit || 0).toLocaleString('sv-SE')} kr`, 
        `Idag: ${Number(dashboardData.daily_net_profit || 0).toLocaleString('sv-SE')} kr`
      ]
    },
    { 
      icon: '🛒', 
      value: Number(dashboardData.total_orders || 0).toString(), 
      label: 'Totalt beställningar',
      trend: 'neutral' as const,
      trendValue: Number(dashboardData.not_shipped || 0) > 0 ? `${dashboardData.not_shipped} väntar` : 'Alla skickade',
      subStats: [
        `Ej skickade: ${dashboardData.not_shipped || 0}`, 
        `Skickade idag: ${dashboardData.shipped_today || 0}`
      ]
    },
    { 
      icon: '📦', 
      value: Number(dashboardData.total_products || 0).toString(), 
      label: 'Produkter i sortiment',
      trend: 'neutral' as const,
      trendValue: `${dashboardData.active_products || 0} aktiva`,
      subStats: [
        `Aktiva: ${dashboardData.active_products || 0}`, 
        `Lågt lager: ${dashboardData.low_stock_products || 0}`
      ]
    },
    { 
      icon: '📊', 
      value: `${Math.round(Number(dashboardData.avg_order_value || 0))} kr`, 
      label: 'Genomsnittligt ordervärde',
      trend: 'neutral' as const,
      trendValue: Number(dashboardData.total_orders || 0) > 0 ? 'Beräknat' : 'Ingen data',
      subStats: [
        `Totalt: ${dashboardData.total_orders || 0} ordrar`, 
        `Skickade veckan: ${dashboardData.shipped_this_week || 0}`
      ]
    }
  ] : [];

  const kpis = dashboardData ? [
    { value: Number(dashboardData.not_shipped || 0).toString(), label: 'Ej skickade ordrar' },
    { value: Number(dashboardData.shipped_today || 0).toString(), label: 'Skickade ordrar idag' },
    { value: Number(dashboardData.shipped_this_week || 0).toString(), label: 'Skickade ordrar denna vecka' },
    { value: `${Number(dashboardData.monthly_revenue || 0).toLocaleString('sv-SE')} kr`, label: 'Omsättning denna månad' },
    { value: `${Math.round(Number(dashboardData.avg_order_value || 0)).toLocaleString('sv-SE')} kr`, label: 'Genomsnittligt ordervärde' },
    { value: Number(dashboardData.low_stock_products || 0).toString(), label: 'Produkter med lågt lager' }
  ] : [];

  const recentActivity = dashboardData ? [
    { 
      type: 'Systemstatus', 
      details: 'Dashboard uppdaterad med live-data', 
      time: 'Precis nu',
      amount: ''
    },
    { 
      type: 'Statistik', 
      details: `${dashboardData.total_orders} totala beställningar`, 
      time: 'Live data',
      amount: `${Number(dashboardData.total_revenue || 0).toLocaleString('sv-SE')} kr`
    },
    { 
      type: 'Produkter', 
      details: `${dashboardData.active_products} aktiva av ${dashboardData.total_products} totalt`, 
      time: 'Live data',
      amount: ''
    },
    { 
      type: 'Försändelser', 
      details: `${dashboardData.not_shipped} väntar på att skickas`, 
      time: 'Live data',
      amount: ''
    },
    { 
      type: 'Prestanda', 
      details: `Genomsnitt: ${Math.round(dashboardData.avg_order_value || 0)} kr per order`, 
      time: 'Beräknat',
      amount: ''
    }
  ] : [];

  const bestSellingProducts = topProducts
    .map(product => ({
      name: product?.name || 'Okänd produkt',
      totalSold: Number(product?.total_sold || 0),
      totalRevenue: Number(product?.total_revenue || 0)
    }))
    .filter(product => product.totalSold > 0)
    .sort((a, b) => b.totalSold - a.totalSold || b.totalRevenue - a.totalRevenue);

  const displayTopProducts = bestSellingProducts.length > 0
    ? bestSellingProducts.map(product => ({
        name: product.name,
        emoji: '📦',
        sales: `${product.totalSold} sålda`,
        revenue: `${product.totalRevenue.toLocaleString('sv-SE')} kr i omsättning`
      }))
    : [
        { 
          name: 'Ingen försäljning registrerad ännu', 
          emoji: '📦', 
          sales: 'När försäljning registreras visas den här.', 
          revenue: '' 
        }
      ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>⏳</div>
        <p>Laddar dashboard-data...</p>
      </div>
    );
  }

  return (
    <div>
      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index} trend={stat.trend}>
            <div className="header">
              <div className="icon">{stat.icon}</div>
              <div className="trend">{stat.trendValue}</div>
            </div>
            <div className="value">{stat.value}</div>
            <div className="label">{stat.label}</div>
            <div className="sub-stats">
              {stat.subStats.map((subStat, i) => (
                <span key={i}>{subStat}</span>
              ))}
            </div>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartsSection>
        <ChartCard>
          <h3>
            📈 Försäljningsutveckling
            <select 
              style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-1)' }}
              value={chartPeriod}
              onChange={(e) => setChartPeriod(e.target.value)}
            >
              <option>Senaste 30 dagarna</option>
              <option>Senaste 3 månaderna</option>
              <option>Senaste året</option>
            </select>
          </h3>
          <SalesChart period={chartPeriod} />
        </ChartCard>

        <TopProducts>
          <h3>🏆 Vilken produkt säljer mest?</h3>
          {displayTopProducts.map((product, index) => (
            <div key={index} className="product-item">
              <div className="product-emoji">{product.emoji}</div>
              <div className="product-info">
                <div className="name">{product.name}</div>
                <div className="sales">{product.sales}</div>
              </div>
              <div className="revenue">{product.revenue}</div>
            </div>
          ))}
        </TopProducts>
      </ChartsSection>

      <DashboardGrid>
        <ChartCard>
          <h3>📊 Nyckeltal (KPI:er)</h3>
          <KPIGrid>
            {kpis.map((kpi, index) => (
              <KPICard key={index}>
                <div className="kpi-value">{kpi.value}</div>
                <div className="kpi-label">{kpi.label}</div>
              </KPICard>
            ))}
          </KPIGrid>
        </ChartCard>

        <RecentActivity>
          <h3>🔔 Senaste aktivitet</h3>
          {recentActivity.map((activity, index) => (
            <div key={index} className="activity-item">
              <div className="activity-content">
                <div className="activity-type">{activity.type}</div>
                <div className="activity-details">{activity.details}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {activity.amount && <div style={{ fontWeight: '500', color: 'var(--color-secondary)' }}>{activity.amount}</div>}
                <div className="time">{activity.time}</div>
              </div>
            </div>
          ))}
        </RecentActivity>
      </DashboardGrid>
    </div>
  );
};

export default AdminDashboard;
